import { useEffect } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Ban, Flag, Frame, Hash, ImageIcon, Monitor, Moon, RotateCcw, Shirt, Sparkles, Square, Stamp, Wand2 } from "lucide-react";
import { useEvent } from "@/context/EventContext";

const THEME_ICONS = { clean: Moon, classic: ImageIcon, creative: Sparkles };
const PLACEMENT_ICONS = { corner: Stamp, flag: Flag, screen: Monitor, tshirt: Shirt };

const Pill = ({ active, onClick, icon: Icon, children, testId }) => (
  <button
    onClick={onClick}
    aria-pressed={active}
    className={`btn px-4 py-2.5 text-sm ${active ? "btn-accent" : "btn-ghost"}`}
    data-testid={testId}
  >
    {Icon && <Icon size={16} />} {children}
  </button>
);

const Group = ({ title, children, testId }) => (
  <div className="mt-5" data-testid={testId}>
    <p className="mb-2 text-sm font-semibold text-slate-300">{title}</p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Preview = ({ previewUrl, options, onChange, onRetake, onGenerate }) => {
  const { event, refresh } = useEvent();
  useEffect(() => {
    if (!event?.logo_placements?.length) refresh();
  }, [event, refresh]);
  const set = (patch) => onChange({ ...options, ...patch });
  const placement = (event?.logo_placements || []).find((p) => p.key === options.logo_placement);
  return (
    <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md" data-testid="preview-screen">
      <p className="eyebrow">Adım 2 / 2</p>
      <h1 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-4xl">Baskını ayarla.</h1>
      <div className="glass mx-auto mt-5 aspect-[3/4] max-h-[34vh] overflow-hidden">
        <img src={previewUrl} alt="Çekilen fotoğraf" className="h-full w-full object-cover" data-testid="preview-image" />
      </div>

      <Group title="Sahne" testId="theme-selector">
        {(event?.themes || []).map((t) => (
          <Pill key={t.key} active={options.theme === t.key} onClick={() => set({ theme: t.key })} icon={THEME_ICONS[t.key]} testId={`theme-${t.key}-button`}>
            {t.label}
          </Pill>
        ))}
      </Group>

      <Group title="Baskıya eklenecek logo" testId="logo-mode-selector">
        <Pill active={options.logo_mode === "event"} onClick={() => set({ logo_mode: "event" })} icon={BadgeCheck} testId="logo-mode-event-button">
          Etkinlik Logosu
        </Pill>
        <Pill active={options.logo_mode === "none"} onClick={() => set({ logo_mode: "none" })} icon={Ban} testId="logo-mode-none-button">
          Logosuz
        </Pill>
      </Group>

      {options.logo_mode === "event" && (
        <>
          <Group title="Logo nerede görünsün?" testId="placement-selector">
            {!event?.logo_placements?.length && <span className="text-xs text-slate-400" data-testid="placement-loading">Seçenekler yükleniyor…</span>}
            {(event?.logo_placements || []).map((p) => (
              <Pill key={p.key} active={options.logo_placement === p.key} onClick={() => set({ logo_placement: p.key })} icon={PLACEMENT_ICONS[p.key]} testId={`placement-${p.key}-button`}>
                {p.label}
              </Pill>
            ))}
          </Group>
          {placement && <p className="mt-2 text-xs text-slate-400" data-testid="placement-description">{placement.description}</p>}
        </>
      )}

      <Group title="Baskı stili" testId="print-style-selector">
        <Pill active={options.print_style === "full"} onClick={() => set({ print_style: "full" })} icon={Square} testId="print-style-full-button">
          Tam Kare
        </Pill>
        <Pill active={options.print_style === "frame"} onClick={() => set({ print_style: "frame" })} icon={Frame} testId="print-style-frame-button">
          Instax Çerçevesi
        </Pill>
      </Group>

      <Group title="Ekstralar" testId="extras-selector">
        <Pill active={options.hashtag} onClick={() => set({ hashtag: !options.hashtag })} icon={Hash} testId="hashtag-toggle">
          {event?.hashtag || "#hashtag"} ekle
        </Pill>
      </Group>

      <div className="mt-5">
        <p className="mb-2 text-sm font-semibold text-slate-300">
          Kütüphane ismi <span className="font-normal text-slate-500">(fotoğrafta görünmez)</span>
        </p>
        <input className="input" placeholder="ör. Ayşe & Mehmet" value={options.label} maxLength={60} onChange={(e) => set({ label: e.target.value })} data-testid="label-input" />
      </div>

      <div className="mt-6 flex gap-3">
        <button className="btn btn-ghost" onClick={onRetake} data-testid="retake-button">
          <RotateCcw size={18} /> Yeniden çek
        </button>
        <button className="btn btn-accent flex-1" onClick={onGenerate} data-testid="generate-button">
          <Wand2 size={18} /> Karikatür oluştur
        </button>
      </div>
    </motion.section>
  );
};
